import '../Button.scss';

import { PopupApiDto } from '@b2x/storefront-api-js-client/src/dto';
import classnames from 'classnames';
import React from 'react';

import { AssetAsBackgroundV2 } from '../AssetAsBackgroundV2';
import { Col, ResponsiveColSize } from '../Col';
import { BackgroundContentSection, PopupContentTypeV2, ResponsiveColumnSizeContentSection } from '../contentTypes';
import { Div } from '../HTMLElement';
import { AlignItems, JustifyContent } from '../interfaces';
import { Row } from '../Row';
import { buildSizeClassNamesFromContent, buildSpacingClassNamesFromContent } from '../util';
import { PropsWithCustomComponent, VariantsController } from '../VariantsController';
import { PopupBuilderAssetComponent } from './PopupBuilderAssetComponent';
import { PopupBuilderRichTextComponent } from './PopupBuilderRichTextComponent';
import { PopupBuilderTextComponent } from './PopupBuilderTextComponent';

export interface PopupBuilderProps {
  popup: PopupApiDto<PopupContentTypeV2>;
}

const PopupBuilder = ({ popup }: PopupBuilderProps) => {
  const mapResponsiveColumnSize = (input: ResponsiveColumnSizeContentSection): ResponsiveColSize => {
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const;

    const output: ResponsiveColSize = {};

    for (const bp of breakpoints) {
      const section = input[bp];
      output[bp] = section?.columnSize ?? '';
    }

    return output;
  };

  return (
    <Div className="popup-builder">
      {(popup.content?.body.background?.asset?.type || popup.content?.body.background?.variant) && (
        <PopupBackground {...popup.content.body.background} />
      )}
      {popup.content?.body.blocks && popup.content.body.blocks.length > 0 && (
        <Div style={{ position: 'relative' }}>
          <Row>
            {popup.content.body.blocks.map((block, index) => {
              const enabled = block.options?.enabled;
              const size = block.options?.size && mapResponsiveColumnSize(block.options.size);
              const offset = buildSizeClassNamesFromContent('offset', block.options?.offset);
              const margin = buildSpacingClassNamesFromContent('m', block.options?.margin);
              const padding = buildSpacingClassNamesFromContent('p', block.options?.padding);

              const justifyContent = block.options?.justifyContent as JustifyContent;
              const alignItems = block.options?.alignItems as AlignItems;

              return (
                <React.Fragment key={block.contentSectionId}>
                  {enabled && (
                    <Col size={size} uiClassName={{ bs5: classnames(offset) }}>
                      <Div
                        alignItems={alignItems}
                        display={'flex'}
                        justifyContent={justifyContent}
                        style={{ position: 'relative' }}
                        uiClassName={{ bs5: classnames(margin, padding, 'h-100') }}
                      >
                        {(block.options?.background?.asset?.type || block.options?.background?.variant) && (
                          <PopupBackground {...block.options.background} />
                        )}
                        <Div style={{ position: 'relative' }}>
                          {block.component?.map((singleBlock) => (
                            <React.Fragment key={singleBlock.contentSectionId}>
                              {singleBlock.text !== undefined && <PopupBuilderTextComponent {...singleBlock.text} />}
                              {singleBlock.richText !== undefined && (
                                <PopupBuilderRichTextComponent {...singleBlock.richText} />
                              )}
                              {singleBlock.asset !== undefined && <PopupBuilderAssetComponent {...singleBlock.asset} />}
                            </React.Fragment>
                          ))}
                        </Div>
                      </Div>
                    </Col>
                  )}
                </React.Fragment>
              );
            })}
          </Row>
        </Div>
      )}
    </Div>
  );
};

export type PopupBuilderVariants = '';

const PopupBuilderController = (props: PropsWithCustomComponent<PopupBuilderProps>) => (
  <VariantsController<PopupBuilderProps, PopupBuilderVariants>
    {...props}
    variantsControllerConfig={{
      defaultComponent: PopupBuilder,
      name: 'PopupBuilder',
    }}
  />
);
export { PopupBuilderController as PopupBuilder };

const PopupBackground = ({ asset, variant }: BackgroundContentSection) => {
  return (
    <Div
      className={classnames('background', { [`bg-${variant}`]: variant })}
      style={{ height: '100%', left: 0, position: 'absolute', top: 0, width: '100%' }}
    >
      {asset && <AssetAsBackgroundV2 {...asset} />}
    </Div>
  );
};
